import { useCallback, useEffect, useState } from 'react';
import { Mail, Loader2, Send, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api.js';

/**
 * Connects one app to its own Resend account, for mail to visitors.
 *
 * Not to be confused with the "Email alerts" block elsewhere on this page:
 * that one runs on the deployment's own Resend and notifies *agents*. This one
 * runs on the app owner's account and reaches *their customers*, which is why
 * it asks for a sender name and address — a merchant's customer should see the
 * merchant, not us.
 *
 * The API key is write-only, exactly like the Slack bot token: it goes up and
 * never comes back, and re-entering it is how you rotate it.
 */
export function ResendCard({ appId, canEdit }) {
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const { integration: found } = await api.resend.get(appId);
      setIntegration(found);
      // Pre-fill so editing the sender name does not mean retyping the address.
      setFromEmail(found?.fromEmail ?? '');
      setFromName(found?.fromName ?? '');
      setReplyTo(found?.replyTo ?? '');
      setLoadError(null);
    } catch (err) {
      // A failed read is not the same fact as "not connected", and showing it
      // as one would tell someone with live email that they have none.
      setIntegration(null);
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    load();
  }, [load]);

  const connect = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await api.resend.connect(appId, {
        apiKey: apiKey.trim(),
        fromEmail: fromEmail.trim(),
        fromName: fromName.trim(),
        replyTo: replyTo.trim(),
      });
      setApiKey('');
      // The key was good but the domain is not verified in that account — the
      // connection is stored, and this is the one thing left to do.
      setNotice(
        saved.senderVerified
          ? 'Connected. A confirmation was sent to your address.'
          : `Key accepted, but a test send from ${saved.fromEmail} failed: ${saved.senderError}. Verify that domain in Resend.`
      );
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        'Disconnect this sender? The stored API key is deleted and this app can no longer email visitors.'
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.resend.disconnect(appId);
      setApiKey('');
      await load();
      setNotice('Disconnected. This app will not send customer email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.resend.test(appId);
      setNotice(
        result.sent ? `Sent to ${result.to}.` : `Resend refused it: ${result.error}`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">
          <Mail size={13} /> Customer email
        </span>
        {integration?.connected && (
          <span className="card__meta mono">{integration.sender}</span>
        )}
      </div>

      {loading ? (
        <div className="card__foot">
          <Loader2 size={13} className="spin" /> Loading…
        </div>
      ) : (
        <>
          {loadError && (
            <div className="result result--warn">
              <AlertCircle size={13} /> Could not read this app&apos;s email settings:{' '}
              {loadError}. Any existing connection is still live — this is the dashboard
              failing to load it, not the sender being disconnected.
            </div>
          )}

          {!integration && !loadError && (
            <div className="card__foot">
              Connect this app&apos;s own Resend account to email visitors from your address —
              used when you reply to someone who has gone offline. Separate from the agent
              alerts above, which come from this Superchat deployment.
            </div>
          )}

          {integration && !integration.connected && (
            <div className="result result--warn">
              <AlertCircle size={13} /> Resend rejected this key: {integration.lastError}.
              Reconnect below with a new one.
            </div>
          )}

          {integration && (
            <div className="settings">
              <div className="settings__row">
                <span>Sends as</span>
                <span className="mono">{integration.sender}</span>
              </div>

              {integration.replyTo && (
                <div className="settings__row">
                  <span>Replies go to</span>
                  <span className="mono">{integration.replyTo}</span>
                </div>
              )}

              <div className="settings__row">
                <span>Last sent</span>
                <span className="mono muted">
                  {integration.lastSentAt
                    ? new Date(integration.lastSentAt).toLocaleString()
                    : 'Nothing yet'}
                </span>
              </div>

              {canEdit && (
                <div className="settings__row">
                  <span>Check the connection</span>
                  <span className="settings__control">
                    <button type="button" className="button" onClick={sendTest} disabled={busy}>
                      <Send size={13} /> Send a test
                    </button>
                    <button
                      type="button"
                      className="button button--danger"
                      onClick={disconnect}
                      disabled={busy}
                    >
                      <Trash2 size={13} /> Disconnect
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}

          {error && <div className="login__error">{error}</div>}
          {notice && (
            <div className="result result--ok">
              <CheckCircle2 size={13} /> {notice}
            </div>
          )}

          {canEdit && (
            <form onSubmit={connect}>
              <div className="card__body">
                <label className="field">
                  <span className="field__label">RESEND API KEY</span>
                  <input
                    className="field__control mono"
                    type="password"
                    required
                    autoComplete="off"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={integration ? 'Enter a new key to rotate' : 're_…'}
                  />
                </label>

                <label className="field">
                  <span className="field__label">
                    SENDER ADDRESS · MUST BE ON A VERIFIED DOMAIN
                  </span>
                  <input
                    className="field__control mono"
                    type="email"
                    required
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    placeholder="support@yourdomain.com"
                  />
                </label>

                <label className="field">
                  <span className="field__label">SENDER NAME · WHAT CUSTOMERS SEE</span>
                  <input
                    className="field__control"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    placeholder="Acme Support"
                  />
                </label>

                <label className="field">
                  <span className="field__label">REPLY-TO · OPTIONAL</span>
                  <input
                    className="field__control mono"
                    type="email"
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    placeholder="help@yourdomain.com"
                  />
                </label>
              </div>

              <div className="card__actions">
                <button type="submit" className="button button--primary" disabled={busy}>
                  {busy ? <Loader2 size={13} className="spin" /> : <Mail size={13} />}
                  {integration ? 'Update sender' : 'Connect'}
                </button>
              </div>
            </form>
          )}

          {!canEdit && (
            <div className="card__foot">
              Your access to this app is read-only for integration, so the sender cannot be
              changed here.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ResendCard;
